import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Others
import { isEmpty, isEqual } from "lodash";

// Hooks
import { useUnmount } from "ahooks";
import useBriefInitialFetch from "./useBriefInitialFetch";

// Store
import { useRedirectOnProjectError } from "utils/hooks";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { clearChart, fetchCharts, getCharts } from "store/slice/charts";
import { toggleLoader } from "store/slice/UI";
import { initialBriefData } from "store/slice/Project/projectData/ProjectSlice";
import { getInsightById } from "store/slice/Project/insights/insightsSlice";
import { updateBrief, getBriefById } from "store/slice/Project/briefs/briefsSlice";

// Utils
import { extractFacetData } from "utils";
import { shallowEqual } from "react-redux";
import { convertBriefData } from "./Brief.functions";
import useGraphsFetch from "utils/hooks/useGraphsFetch";

// Data
import { urlPaths } from "enums/urlPaths";
import { getInsightFilters } from "pages/ProjectDashboard/adapters/formattedData";
import { TBriefData } from "store/slice/Project/projectData/ProjectSlice.types";

// Services
import { updateProjectByPathService } from "services";

const errorInitialValue = { id: undefined, message: "" };

type TBriefError = {
    id: undefined | "STEP1" | "STEP2" | "STEP2A" | "STEP2B" | "STEP3";
    chart1?: boolean;
    chart2?: boolean;
    message: string;
};

const useBrief = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    const { companyId: companyIdParams } = useParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const briefId = searchParams.get("briefId");

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError([projectId, insightId, briefId]);

    // Data
    const { data: chartData, status: chartStatus } = useAppSelector(getCharts);
    const { data: brief } = useAppSelector(getBriefById(Number(briefId)), shallowEqual);
    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);
    const companyId = companyIdParams || getInsightFilters(insightData)?.data.company.id || undefined;

    // Brief Data
    const convertedBrief = convertBriefData(brief) || initialBriefData;
    const [briefData, setBriefData] = useState<TBriefData>(convertedBrief);

    // Charts flags
    const graphData = useGraphsFetch(["LD4", "LD5"], false);
    const { target_company_board: selectedBoard } = graphData;
    const hasDataForRequest = Boolean(companyId && selectedBoard.length);

    // Other flags
    const [error, setError] = useState<TBriefError>(errorInitialValue);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const shouldDisableFiltersButton = useMemo(() => isEqual(briefData, convertedBrief), [briefData, convertedBrief]);

    const handleClearError = () => {
        setError(errorInitialValue);
    };

    const handleSetBriefData = (data: Partial<TBriefData> | undefined) => {
        if (!data) {
            return;
        }
        setBriefData({ ...briefData, ...data });
    };

    const handleGoToDashboard = () => {
        navigate(`${urlPaths.ProjectDashboard}/${projectId}`);
    };

    const handleConfirmationModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    const handleResetFilters = () => {
        setBriefData(convertedBrief);
    };

    const handleSaveBrief = async () => {
        if (!briefData) {
            return;
        }

        try {
            dispatch(toggleLoader(true));

            const payload = {
                data: {
                    crm_vacancy: briefData.position,
                    role_function: briefData.roleFunction,
                    role_functions: [briefData.roleFunction],
                    role_level: briefData.roleLevel,
                    role_levels: [briefData.roleLevel],
                    functional_balance: briefData.chart1,
                    individual_positioning: briefData.chart2,
                    facet_situational: {
                        narratives: extractFacetData(briefData.facets.situational),
                    },
                    facet_domain: {
                        narratives: extractFacetData(briefData.facets.domain),
                    },
                    facet_functional: {
                        narratives: extractFacetData(briefData.facets.functional),
                    },
                },
                meta_ui: {
                    facet_situational: {
                        narratives: extractFacetData(briefData.facets.situational),
                    },
                    facet_domain: {
                        narratives: extractFacetData(briefData.facets.domain),
                    },
                    facet_functional: {
                        narratives: extractFacetData(briefData.facets.functional),
                    },
                },
            };

            await updateProjectByPathService({
                id: Number(projectId),
                project_path: `/project_${projectId}/brief_list/brief_${briefId}`,
                data: payload,
            });

            if (insightId && brief) {
                const newFacets = {
                    facet_situational: {
                        ...brief.data.facet_situational,
                        narratives: brief.data.facet_situational.narratives.map((narrative) => {
                            const weight = payload.data.facet_situational.narratives.find(
                                (payloadNarrative) => payloadNarrative.type === narrative.type.type
                            )?.weight as number;
                            return { ...narrative, weight };
                        }),
                    },
                    facet_domain: {
                        ...brief.data.facet_domain,
                        narratives: brief.data.facet_domain.narratives.map((narrative) => {
                            const weight = payload.data.facet_domain.narratives.find(
                                (payloadNarrative) => payloadNarrative.type === narrative.type.type
                            )?.weight as number;
                            return { ...narrative, weight };
                        }),
                    },
                    facet_functional: {
                        ...brief.data.facet_functional,
                        narratives: brief.data.facet_functional.narratives.map((narrative) => {
                            const weight = payload.data.facet_functional.narratives.find(
                                (payloadNarrative) => payloadNarrative.type === narrative.type.type
                            )?.weight as number;
                            return { ...narrative, weight };
                        }),
                    },
                };

                const updatedData = {
                    ...brief,
                    data: { ...brief.data, ...payload.data, ...newFacets },
                    meta_ui: payload.meta_ui,
                };
                dispatch(updateBrief(updatedData));
            }
        } catch (error) {
            console.error(error);
        } finally {
            dispatch(toggleLoader(false));
        }
    };

    const handleScrollTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const handleValidateFields = async (data: TBriefData | undefined) => {
        if (!data) return;

        if (isEmpty(data.position) || isEmpty(data.roleFunction) || isEmpty(data.roleLevel)) {
            setError({
                id: "STEP1",
                message:
                    "Please complete the position in Step 1 by selecting the vacancy, defining the function of the role and seniority level.",
            });

            handleScrollTop();
            return;
        }

        if (
            Object.values(data.chart1).some((value) => value === null) &&
            Object.values(data.chart2).some((value) => value === null)
        ) {
            setError({
                id: "STEP2",
                chart1: true,
                chart2: true,
                message:
                    "Please complete Step 2 by selecting where on the Functional Balance and Individual Positioning charts you would like your ideal candidate to sit.",
            });

            handleScrollTop();
            return;
        }
        if (Object.values(data.chart1).some((value) => value === null)) {
            setError({
                id: "STEP2A",
                chart1: true,
                chart2: false,
                message:
                    "Please select where on the Functional Balance chart you would like your ideal candidate to sit.",
            });

            handleScrollTop();
            return;
        }
        if (Object.values(data.chart2).some((value) => value === null)) {
            setError({
                id: "STEP2B",
                chart1: false,
                chart2: true,
                message:
                    "Please select where on the Individual Positioning chart you would like your ideal candidate to sit.",
            });

            handleScrollTop();
            return;
        }
        if (
            [...data.facets.situational, ...data.facets.domain, ...data.facets.functional].some(
                (facet) => !facet.weight
            )
        ) {
            setError({
                id: "STEP3",
                message: "Please complete scoring your facets of experience in Step 3, to move forward.",
            });

            handleScrollTop();
            return;
        }

        setError(errorInitialValue);

        await handleSaveBrief();
        dispatch(toggleLoader(false));
        navigate(
            `${urlPaths.BriefReview}/${companyId}?projectId=${projectId}&insightId=${insightId}&briefId=${briefId}`
        );
    };

    useUnmount(() => {
        dispatch(clearChart());
    });

    // Initial fetch
    useBriefInitialFetch(setBriefData);

    // Fetch charts
    useEffect(() => {
        const fetchGraphs = async () => {
            try {
                dispatch(toggleLoader(true));
                await dispatch(
                    fetchCharts({ graphData, projectId: projectId as string, insightId: insightId as string })
                );
            } catch (error) {
                console.error(error);
            } finally {
                dispatch(toggleLoader(false));
            }
        };

        if (
            hasDataForRequest &&
            projectId &&
            insightId &&
            isEmpty(chartData?.graphs.LD5) &&
            isEmpty(chartData?.graphs.LD4) &&
            chartStatus === "pristine"
        ) {
            fetchGraphs();
        }
    }, [chartData, chartStatus, dispatch, graphData, hasDataForRequest, insightId, projectId]);

    return {
        isModalOpen,
        error,
        briefData,
        chartStatus,
        shouldDisableFiltersButton,
        handleConfirmationModal,
        handleGoToDashboard,
        handleValidateFields,
        handleClearError,
        handleSetBriefData,
        handleResetFilters,
    };
};

export default useBrief;
